'use client'

import React, { useCallback, useMemo } from 'react'
import { BN } from '@coral-xyz/anchor'
import { Box, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { TREASURY_WALLET_ADDRESS_SOL } from '@utils/constants'
import { HNT_MINT, IOT_MINT, MOBILE_MINT, toNumber } from '@helium/spl-utils'
import {
  DewiBreakpointsLg,
  DewiBreakpointsSm,
  DewiBreakpointsXl,
} from '@furry/tokens'
import { HotspotsProvider, useHotspots } from '@hooks/useHotspots'
import { TokenPricesProvider, useTokenPrices } from '@hooks/useTokenPrices'
import OpenLinkIcon from '@assets/openLinkIcon.svg'
import { useConnection } from '@solana/wallet-adapter-react'
import NavigationButtonGroup from '@components/NavigationButtonGroup'
import { usePathname, useRouter } from 'next/navigation'
import { TokensProvider, useTokens } from '@hooks/useTokens'

const LayoutRoot = styled('div')(() => ({
  position: 'relative',
  top: '100px',
}))

const TreasuryTitle = styled(Typography)(() => ({}))

const TotalHoldingsBalanceLabel = styled(Typography)(({ theme }) => ({
  color: '#666666',
}))

const TotalHoldingsBalanceAmount = styled(Typography)(({ theme }) => ({
  color: '#FCFCFA',
}))

const HeaderLayout = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down(parseInt(DewiBreakpointsSm))]: {
    paddingLeft: '1rem',
    paddingRight: '1rem',
    gap: theme.spacing(2),
  },
  [theme.breakpoints.down(parseInt(DewiBreakpointsLg))]: {
    flexDirection: 'column',
    paddingLeft: '1.5rem',
    paddingRight: '1rem',
  },
  [theme.breakpoints.between(
    parseInt(DewiBreakpointsLg),
    parseInt(DewiBreakpointsXl),
  )]: {
    flexDirection: 'column',
    paddingLeft: '1rem',
  },
  [theme.breakpoints.up(parseInt(DewiBreakpointsXl))]: {
    flexDirection: 'column',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
  },
  display: 'flex',
  gap: theme.spacing(1),
}))

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 1,
}))

const TreasuryLayout = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation()
  const { totalPendingRewards } = useHotspots()
  const { tokenPrices } = useTokenPrices()
  const { tokenAccounts, loading: tokensLoading } = useTokens()
  const router = useRouter()

  const navRoutes = [
    {
      name: t('treasuryPage.tokens'),
      isNav: true,
    },
    {
      name: t('treasuryPage.miners'),
      isNav: true,
    },
  ]

  const pendingMobileRewards = useMemo(
    () => toNumber(new BN(totalPendingRewards[`${MOBILE_MINT.toBase58()}`]), 6),
    [totalPendingRewards],
  )
  const pendingIotRewards = useMemo(
    () => toNumber(new BN(totalPendingRewards[`${IOT_MINT.toBase58()}`]), 6),
    [totalPendingRewards],
  )
  const pendingHntRewards = useMemo(
    () => toNumber(new BN(totalPendingRewards[`${HNT_MINT.toBase58()}`]), 8),
    [totalPendingRewards],
  )

  const totalFiatBalance = useMemo(() => {
    if (tokensLoading || !tokenAccounts.length) return undefined

    const held = tokenAccounts.reduce((acc, tokenAccount) => {
      const price = tokenPrices[tokenAccount.mint]?.price || 0
      try {
        return (
          acc +
          price *
            toNumber(
              new BN(tokenAccount?.balance.toString()),
              tokenAccount.decimals,
            )
        )
      } catch (error) {
        return acc
      }
    }, 0)

    // Rewards sitting with the oracle are part of the treasury too.
    const pending =
      pendingMobileRewards * (tokenPrices[MOBILE_MINT.toBase58()]?.price || 0) +
      pendingIotRewards * (tokenPrices[IOT_MINT.toBase58()]?.price || 0) +
      pendingHntRewards * (tokenPrices[HNT_MINT.toBase58()]?.price || 0)

    return held + pending
  }, [
    tokenAccounts,
    tokenPrices,
    tokensLoading,
    pendingMobileRewards,
    pendingIotRewards,
    pendingHntRewards,
  ])

  const { connection } = useConnection()

  const isDevnet = useMemo(() => {
    return connection.rpcEndpoint.includes('devnet')
  }, [connection?.rpcEndpoint])

  const openExplorerLink = useCallback(() => {
    window.open(
      `https://explorer.solana.com/address/${TREASURY_WALLET_ADDRESS_SOL.toBase58()}${isDevnet ? '?cluster=devnet' : ''}`,
      '_blank',
    )
  }, [isDevnet])

  const pathname = usePathname()

  const getActiveRoute = useCallback(() => {
    const route = navRoutes.find((route) => pathname.includes(route.name))
    return route?.name || navRoutes[0].name
  }, [pathname])

  const activeRoute = useMemo(getActiveRoute, [pathname])

  const handleCurrentSelection = useCallback(
    (value: string) => {
      router.push(`/treasury/${value}`)
    },
    [router],
  )

  return (
    <LayoutRoot>
      <Container>
        <HeaderLayout>
          <div className="flex flex-row gap-2 items-center">
            <TreasuryTitle variant="h1">
              {t('treasuryPage.title')}
            </TreasuryTitle>
            <button
              onClick={openExplorerLink}
              className="hover:text-gray-1 text-gray-6"
            >
              <OpenLinkIcon
                style={{
                  width: '32px',
                  height: '32px',
                  color: 'inherit',
                }}
              />
            </button>
          </div>
          <div className="flex flex-row">
            <div className="flex flex-col">
              <TotalHoldingsBalanceLabel variant="h6">
                {t('treasuryPage.totalHoldingsBalance')}
              </TotalHoldingsBalanceLabel>
              <TotalHoldingsBalanceAmount variant="h5">
                {totalFiatBalance
                  ? `$${totalFiatBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} USD`
                  : '-'}
              </TotalHoldingsBalanceAmount>
            </div>
          </div>
          <div className="mt-2">
            <NavigationButtonGroup
              routes={navRoutes}
              onClick={handleCurrentSelection}
              selected={activeRoute}
            />
          </div>
        </HeaderLayout>
        {children}
      </Container>
    </LayoutRoot>
  )
}

const TreasuryLayoutWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <HotspotsProvider>
      <TokensProvider>
        <TokenPricesProvider>
          <TreasuryLayout>{children}</TreasuryLayout>
        </TokenPricesProvider>
      </TokensProvider>
    </HotspotsProvider>
  )
}

export default TreasuryLayoutWrapper
